import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import ErrorState from './ErrorState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { uploadReportDocTemplate, fileIcon } from '../utils/fileUpload';
import { formatDateTime, plural } from '../utils/format';

/**
 * <ReportsSettings /> — manage saved report templates (source + columns + filters)
 * and scheduled email delivery. Rendered inside the Settings "Reports" modal.
 */
const FREQ = ['daily', 'weekly', 'monthly'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NT_INIT = { name: '', source: 'jobs', columns: [], status: '', from: '', to: '', dueBefore: '' };
const NS_INIT = { template_id: '', frequency: 'weekly', day_of_week: 1, day_of_month: 1, hour: 8, recipients: '' };
const EMAIL_RE = /^[^\s@<>(),;:]+@[^\s@<>(),;:]+\.[^\s@<>(),;:]{2,}$/;
// Status values each data source's filter understands (free text otherwise).
const STATUS_OPTIONS = {
  jobs: ['draft', 'invited', 'quoted', 'approved', 'scheduled', 'active', 'completed', 'invoiced', 'paid', 'cancelled'],
  assets: ['Overdue', 'Scheduled', 'Complete', 'Part Complete', 'Not Seen', 'Unknown', 'Not Applicable', 'Decommissioned'],
  staff_quals: ['Valid', 'Expiring', 'Expired', 'No expiry'],
  forms: ['Complete', 'Part Complete', 'Not Started'],
  revenue: ['draft', 'sent', 'paid', 'overdue'],
  outstanding: ['sent', 'overdue'],
};
const cap = (x) => String(x).charAt(0).toUpperCase() + String(x).slice(1);

const ReportsSettings = () => {
  const { confirm } = useConfirm();
  const [tab, setTab] = useState('templates');
  const [loadErr, setLoadErr] = useState(null);
  const [ntErr, setNtErr] = useState({});
  const [nsErr, setNsErr] = useState({});
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [sources, setSources] = useState({});
  const [nt, setNt] = useState(NT_INIT);
  const [showNt, setShowNt] = useState(false);
  const [ns, setNs] = useState(NS_INIT);
  const [showNs, setShowNs] = useState(false);
  const [docTemplates, setDocTemplates] = useState([]);
  const [pdfAvail, setPdfAvail] = useState(true);
  const [showUp, setShowUp] = useState(false);
  const [upName, setUpName] = useState('');
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    let firstErr = null;
    const grab = async (fn) => { try { await fn(); } catch (e) { if (!firstErr) firstErr = e; } };
    await grab(async () => { const d = await apiFetch('/api/contractor/report-sources'); if (d?.sources) setSources(d.sources); });
    await grab(async () => { const d = await apiFetch('/api/contractor/report-templates'); setTemplates(Array.isArray(d?.templates) ? d.templates : []); });
    await grab(async () => { const d = await apiFetch('/api/contractor/report-schedules'); setSchedules(Array.isArray(d?.schedules) ? d.schedules : []); });
    await grab(async () => { const d = await apiFetch('/api/contractor/report-doc-templates'); setDocTemplates(Array.isArray(d?.templates) ? d.templates : []); if (typeof d?.pdf_available === 'boolean') setPdfAvail(d.pdf_available); });
    setLoadErr(firstErr);
  }, []);
  useEffect(() => { load(); }, [load]);

  const srcMeta = sources[nt.source] || { columns: [], filters: [] };
  const templateName = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t.name])), [templates]);

  const toggleCol = (c) => setNt((s) => ({ ...s, columns: s.columns.includes(c) ? s.columns.filter((x) => x !== c) : [...s.columns, c] }));

  const createTemplate = async () => {
    const errs = {};
    if (!nt.name.trim()) errs.name = 'Name the report';
    if (nt.from && nt.to && nt.to < nt.from) errs.to = 'The end date must be on or after the start date';
    setNtErr(errs);
    if (Object.keys(errs).length) return;
    const filters = {};
    if (srcMeta.filters.includes('status') && nt.status.trim()) filters.status = nt.status.trim();
    if (srcMeta.filters.includes('dateRange')) { if (nt.from) filters.from = nt.from; if (nt.to) filters.to = nt.to; }
    if (srcMeta.filters.includes('dueBefore') && nt.dueBefore) filters.dueBefore = nt.dueBefore;
    const config = { columns: nt.columns, filters };
    setSaving(true);
    try {
      await apiFetch('/api/contractor/report-templates', { method: 'POST', body: JSON.stringify({ name: nt.name.trim(), source: nt.source, config, output_format: 'pdf' }) });
      toast.success('Report template created');
      setNt(NT_INIT); setShowNt(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t create the template')); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async (t) => {
    const n = schedules.filter((x) => String(x.template_id) === String(t.id)).length;
    const ok = await confirm({ title: `Delete “${t.name}”?`, body: n ? `Its ${plural(n, 'schedule')} will be deleted too, so those emails stop.` : 'Past runs of it stay in Recent reports.', confirmLabel: 'Delete report', tone: 'danger' });
    if (!ok) return;
    setTemplates((prev) => prev.filter((x) => x.id !== t.id));
    try { await apiFetch(`/api/contractor/report-templates/${t.id}`, { method: 'DELETE' }); toast.success('Template deleted'); load(); }
    catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t delete the template')); load(); }
  };

  const uploadDocTemplate = async () => {
    if (!upFile) { toast.error('Choose a template file'); return; }
    setUploading(true);
    try {
      await uploadReportDocTemplate(upFile, upName.trim());
      toast.success('Document template uploaded');
      setUpName(''); setUpFile(null); setShowUp(false); load();
    } catch (e) { toast.error(e?.message || apiErrorMessage(e, 'Couldn’t upload the template')); }
    finally { setUploading(false); }
  };
  const deleteDocTemplate = async (t) => {
    const ok = await confirm({ title: `Delete document template “${t.name}”?`, body: 'Reports can no longer be exported with it.', confirmLabel: 'Delete template', tone: 'danger' });
    if (!ok) return;
    setDocTemplates((prev) => prev.filter((x) => x.id !== t.id));
    try { await apiFetch(`/api/contractor/report-doc-templates/${t.id}`, { method: 'DELETE' }); toast.success('Template deleted'); load(); }
    catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t delete the template')); load(); }
  };

  const createSchedule = async () => {
    const recipients = ns.recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const errs = {};
    if (!ns.template_id) errs.template_id = 'Choose a report';
    const bad = recipients.find((r) => !EMAIL_RE.test(r));
    if (!recipients.length) errs.recipients = 'Add at least one recipient email';
    else if (bad) errs.recipients = `“${bad}” isn’t a valid email address`;
    else if (recipients.length > 10) errs.recipients = 'Send to at most 10 addresses';
    const dom = Number(ns.day_of_month);
    if (ns.frequency === 'monthly' && (!Number.isInteger(dom) || dom < 1 || dom > 28)) errs.day_of_month = 'Choose a day from 1 to 28';
    setNsErr(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await apiFetch('/api/contractor/report-schedules', { method: 'POST', body: JSON.stringify({
        template_id: Number(ns.template_id), frequency: ns.frequency,
        day_of_week: ns.frequency === 'weekly' ? Number(ns.day_of_week) : null,
        day_of_month: ns.frequency === 'monthly' ? Number(ns.day_of_month) : null,
        hour: Number(ns.hour), recipients, enabled: true }) });
      toast.success('Schedule created');
      setNs(NS_INIT); setShowNs(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t create the schedule')); }
    finally { setSaving(false); }
  };

  const toggleSchedule = async (s) => {
    const enabled = !s.enabled;
    setSchedules((prev) => prev.map((x) => (x.id === s.id ? { ...x, enabled } : x)));
    try {
      await apiFetch(`/api/contractor/report-schedules/${s.id}`, { method: 'PATCH', body: JSON.stringify({ enabled }) });
      toast.success(enabled ? 'Schedule resumed' : 'Schedule paused');
    } catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t change the schedule')); load(); }
  };
  const deleteSchedule = async (s) => {
    const ok = await confirm({ title: 'Delete this schedule?', body: `${templateName[s.template_id] || 'The report'} will no longer be emailed to ${plural((s.recipients || []).length, 'recipient')}.`, confirmLabel: 'Delete schedule', tone: 'danger' });
    if (!ok) return;
    setSchedules((prev) => prev.filter((x) => x.id !== s.id));
    try { await apiFetch(`/api/contractor/report-schedules/${s.id}`, { method: 'DELETE' }); toast.success('Schedule deleted'); }
    catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t delete the schedule')); load(); }
  };

  const cadence = (s) => {
    if (s.frequency === 'daily') return `Daily at ${String(s.hour).padStart(2, '0')}:00`;
    if (s.frequency === 'weekly') return `Weekly · ${DOW[s.day_of_week] || 'Mon'} at ${String(s.hour).padStart(2, '0')}:00`;
    return `Monthly · day ${s.day_of_month || 1} at ${String(s.hour).padStart(2, '0')}:00`;
  };

  const tabs = [['templates', 'Templates'], ['schedules', 'Schedules'], ['documents', 'Documents']];
  const statusChoices = STATUS_OPTIONS[nt.source];

  return (
    <div>
      <div className="cw-segment" role="group" aria-label="Report settings" style={{ marginBottom: 14 }}>
        {tabs.map(([id, label]) => <button key={id} type="button" className={tab === id ? 'active' : ''} aria-pressed={tab === id} onClick={() => setTab(id)}>{label}</button>)}
      </div>
      {loadErr && <div style={{ marginBottom: 12 }}><ErrorState compact thing="some report settings" error={loadErr} onRetry={load} /></div>}

      {tab === 'templates' && (
        <>
          <ul style={{ listStyle: 'none', margin: '0 0 14px', padding: 0, border: '1px solid var(--cw-border)', borderRadius: 12, overflow: 'hidden' }}>
            {templates.map((t, i) => (
              <li key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderTop: i ? '1px solid var(--cw-border-light)' : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 600 }}>{t.name}</div>
                  <div className="cw-meta">{sources[t.source]?.label || t.source}{(t.config?.columns?.length) ? ` · ${plural(t.config.columns.length, 'column')}` : ''}{t.is_builtin ? ' · built-in' : ''}</div>
                </div>
                {!t.is_builtin && <button type="button" className="cw-icon-btn" aria-label={`Delete ${t.name}`} title="Delete" onClick={() => deleteTemplate(t)}><i className="fa-solid fa-trash" aria-hidden="true" style={{ color: 'var(--cw-danger-fg)' }} /></button>}
              </li>
            ))}
            {templates.length === 0 && <li className="cw-meta" style={{ padding: 14 }}>No templates yet.</li>}
          </ul>

          {!showNt ? (
            <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setShowNt(true)}><i className="fa-solid fa-plus" aria-hidden="true" /> New report template</button>
          ) : (
            <div style={{ border: '1px solid var(--cw-border)', background: 'var(--cw-surface-alt, #f8fbff)', borderRadius: 12, padding: 14 }}>
              <Field label="Report name" required error={ntErr.name}><input value={nt.name} maxLength={120} placeholder="e.g. Overdue anchors this month" onChange={(e) => { setNt((s) => ({ ...s, name: e.target.value })); setNtErr({}); }} /></Field>
              <Field label="Data source">
                <select value={nt.source} onChange={(e) => setNt((s) => ({ ...s, source: e.target.value, columns: [], status: '' }))}>
                  {Object.entries(sources).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </Field>
              <fieldset style={{ border: 'none', margin: '0 0 12px', padding: 0 }}>
                <legend className="cw-label">Columns <span className="cw-meta" style={{ fontWeight: 400 }}>(choose none for all)</span></legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {srcMeta.columns.map((c) => {
                    const on = nt.columns.includes(c);
                    return <button key={c} type="button" aria-pressed={on} onClick={() => toggleCol(c)} style={{ minHeight: 36, fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 600, borderRadius: 999, padding: '4px 12px', cursor: 'pointer', border: `1px solid ${on ? 'var(--cw-blue)' : 'var(--cw-border)'}`, background: on ? 'var(--cw-blue)' : 'var(--cw-surface, #fff)', color: on ? '#fff' : 'var(--cw-text-2)' }}>{on && <i className="fa-solid fa-check" aria-hidden="true" style={{ marginRight: 5, fontSize: '0.68rem' }} />}{c}</button>;
                  })}
                </div>
              </fieldset>
              {srcMeta.filters.includes('status') && (
                <Field label="Status filter (optional)">
                  {statusChoices ? (
                    <select value={nt.status} onChange={(e) => setNt((s) => ({ ...s, status: e.target.value }))}>
                      <option value="">Any status</option>
                      {statusChoices.map((x) => <option key={x} value={x}>{cap(x)}</option>)}
                    </select>
                  ) : <input value={nt.status} placeholder="Status" onChange={(e) => setNt((s) => ({ ...s, status: e.target.value }))} />}
                </Field>
              )}
              {srcMeta.filters.includes('dateRange') && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
                  <Field label="From (optional)"><input type="date" value={nt.from} max={nt.to || undefined} onChange={(e) => { setNt((s) => ({ ...s, from: e.target.value })); setNtErr({}); }} /></Field>
                  <Field label="To (optional)" error={ntErr.to}><input type="date" value={nt.to} min={nt.from || undefined} onChange={(e) => { setNt((s) => ({ ...s, to: e.target.value })); setNtErr({}); }} /></Field>
                </div>
              )}
              {srcMeta.filters.includes('dueBefore') && <Field label="Due before (optional)"><input type="date" value={nt.dueBefore} onChange={(e) => setNt((s) => ({ ...s, dueBefore: e.target.value }))} /></Field>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button type="button" className="cw-btn cw-btn-secondary" onClick={() => { setShowNt(false); setNt(NT_INIT); setNtErr({}); }}>Cancel</button>
                <button type="button" className="cw-btn cw-btn-primary" onClick={createTemplate} disabled={saving}><i className="fa-solid fa-plus" aria-hidden="true" /> {saving ? 'Creating…' : 'Create'}</button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'schedules' && (
        <>
          <ul style={{ listStyle: 'none', margin: '0 0 14px', padding: 0, border: '1px solid var(--cw-border)', borderRadius: 12, overflow: 'hidden' }}>
            {schedules.map((s, i) => (
              <li key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderTop: i ? '1px solid var(--cw-border-light)' : 'none', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 600 }}>{templateName[s.template_id] || 'Report'} {!s.enabled && <span className="cw-chip">Paused</span>}</div>
                  <div className="cw-meta">{cadence(s)} · {plural((s.recipients || []).length, 'recipient')}{s.next_run && s.enabled ? ` · next ${formatDateTime(s.next_run)}` : ''}</div>
                </div>
                <button type="button" className="cw-btn cw-btn-secondary" role="switch" aria-checked={!!s.enabled} onClick={() => toggleSchedule(s)}>
                  <i className={`fa-solid ${s.enabled ? 'fa-toggle-on' : 'fa-toggle-off'}`} aria-hidden="true" style={{ color: s.enabled ? 'var(--cw-success-fg)' : 'var(--cw-text-faint)' }} /> {s.enabled ? 'On' : 'Paused'}
                </button>
                <button type="button" className="cw-icon-btn" aria-label={`Delete schedule for ${templateName[s.template_id] || 'report'}`} title="Delete" onClick={() => deleteSchedule(s)}><i className="fa-solid fa-trash" aria-hidden="true" style={{ color: 'var(--cw-danger-fg)' }} /></button>
              </li>
            ))}
            {schedules.length === 0 && <li className="cw-meta" style={{ padding: 14 }}>No scheduled reports. Add one to have it emailed automatically.</li>}
          </ul>

          {!showNs ? (
            <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setShowNs(true)} disabled={!templates.length}><i className="fa-solid fa-plus" aria-hidden="true" /> New schedule</button>
          ) : (
            <div style={{ border: '1px solid var(--cw-border)', background: 'var(--cw-surface-alt, #f8fbff)', borderRadius: 12, padding: 14 }}>
              <Field label="Report" required error={nsErr.template_id}><select value={ns.template_id} onChange={(e) => { setNs((s) => ({ ...s, template_id: e.target.value })); setNsErr({}); }}>
                <option value="">Choose a report…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
                <Field label="Frequency"><select value={ns.frequency} onChange={(e) => setNs((s) => ({ ...s, frequency: e.target.value }))}>{FREQ.map((f) => <option key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</option>)}</select></Field>
                <Field label="Time (UK)"><select value={ns.hour} onChange={(e) => setNs((s) => ({ ...s, hour: e.target.value }))}>{Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}</select></Field>
              </div>
              {ns.frequency === 'weekly' && <Field label="Day of week"><select value={ns.day_of_week} onChange={(e) => setNs((s) => ({ ...s, day_of_week: e.target.value }))}>{DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></Field>}
              {ns.frequency === 'monthly' && <Field label="Day of month (1–28)" error={nsErr.day_of_month}><input type="number" inputMode="numeric" min="1" max="28" value={ns.day_of_month} onChange={(e) => setNs((s) => ({ ...s, day_of_month: e.target.value }))} /></Field>}
              <Field label="Recipients" required error={nsErr.recipients} hint="Separate email addresses with commas — up to 10."><input type="text" inputMode="email" value={ns.recipients} placeholder="fm@client.com, ops@yourco.uk" onChange={(e) => { setNs((s) => ({ ...s, recipients: e.target.value })); setNsErr({}); }} /></Field>
              <p className="cw-meta" style={{ margin: '0 0 10px' }}>Sends the report as a CSV from your connected mailbox (Settings → Connect email).</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="cw-btn cw-btn-secondary" onClick={() => { setShowNs(false); setNs(NS_INIT); setNsErr({}); }}>Cancel</button>
                <button type="button" className="cw-btn cw-btn-primary" onClick={createSchedule} disabled={saving}><i className="fa-solid fa-plus" aria-hidden="true" /> {saving ? 'Creating…' : 'Create schedule'}</button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'documents' && (
        <>
          <p className="cw-meta" style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Upload a Word/Excel template with <code>{'{d.…}'}</code> tags to turn any report into a branded, field-bound document. Design it in Word or LibreOffice — see the tag reference below — then export from the Reports page (Export → your template).
          </p>
          <ul style={{ listStyle: 'none', margin: '0 0 14px', padding: 0, border: '1px solid var(--cw-border)', borderRadius: 12, overflow: 'hidden' }}>
            {docTemplates.map((t, i) => {
              const fi = fileIcon(t.format);
              return (
                <li key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderTop: i ? '1px solid var(--cw-border-light)' : 'none' }}>
                  <i className={fi.icon} aria-hidden="true" style={{ color: fi.color, fontSize: '1.05rem', width: 20 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 600 }}>{t.name}</div>
                    <div className="cw-meta">.{t.format} · {t.file_name}</div>
                  </div>
                  <button type="button" className="cw-icon-btn" aria-label={`Delete document template ${t.name}`} title="Delete" onClick={() => deleteDocTemplate(t)}><i className="fa-solid fa-trash" aria-hidden="true" style={{ color: 'var(--cw-danger-fg)' }} /></button>
                </li>
              );
            })}
            {docTemplates.length === 0 && <li className="cw-meta" style={{ padding: 14 }}>No document templates yet. Upload one to export branded, field-bound reports.</li>}
          </ul>

          {!showUp ? (
            <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setShowUp(true)}><i className="fa-solid fa-upload" aria-hidden="true" /> Upload template</button>
          ) : (
            <div style={{ border: '1px solid var(--cw-border)', background: 'var(--cw-surface-alt, #f8fbff)', borderRadius: 12, padding: 14 }}>
              <Field label="Template name"><input value={upName} maxLength={120} placeholder="e.g. Monthly compliance report" onChange={(e) => setUpName(e.target.value)} /></Field>
              <Field label="File (.docx, .xlsx, .odt, .ods)" required>
                <input type="file" accept=".docx,.xlsx,.odt,.ods,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setUpFile(e.target.files?.[0] || null)} />
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button type="button" className="cw-btn cw-btn-secondary" onClick={() => { setShowUp(false); setUpName(''); setUpFile(null); }}>Cancel</button>
                <button type="button" className="cw-btn cw-btn-primary" disabled={uploading || !upFile} onClick={uploadDocTemplate}><i className={`fa-solid ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'}`} aria-hidden="true" /> {uploading ? 'Uploading…' : 'Upload'}</button>
              </div>
            </div>
          )}

          {!pdfAvail && <p style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', color: 'var(--cw-warning-fg, #92400e)', margin: '12px 2px 0' }}><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> PDF and Word exports from your templates aren’t set up on the server yet. Excel export works.</p>}

          <div style={{ marginTop: 16, border: '1px solid var(--cw-border-light)', borderRadius: 12, background: 'var(--cw-surface-alt, #fbfcfe)', padding: 14 }}>
            <h4 style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', fontWeight: 700, margin: '0 0 8px' }}>Template tag reference</h4>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--cs-text-sm, 0.8125rem)', color: 'var(--cw-text-2)', lineHeight: 1.7 }}>
              <li><code>{'{d.title}'}</code>, <code>{'{d.company.name}'}</code>, <code>{'{d.generatedAt}'}</code>, <code>{'{d.rowCount}'}</code></li>
              <li>Row loop by column: <code>{'{d.records[i].status}'}</code> — column names lowercased, spaces → <code>_</code> (e.g. “Work type” → <code>work_type</code>)</li>
              <li>Positional: <code>{'{d.columns[i]}'}</code> for headers, <code>{'{d.rows[i].cells[j]}'}</code> for cells</li>
              <li>Filters: <code>{'{d.filters[i].label}'}</code> / <code>{'{d.filters[i].value}'}</code></li>
            </ul>
          </div>
        </>
      )}

    </div>
  );
};

export default ReportsSettings;
